<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useCapiStore } from '../store';
import { outcomeLabelKey, outcomeMeta } from '../outcomes';

import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Banner from 'dashboard/components-next/banner/Banner.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useI18n();
const store = useCapiStore();

const inboxId = computed(() => Number(props.inbox.id));
const enabled = ref([]);
const requireBeforeResolve = ref(false);
const required = ref([]);
const inherited = ref(false);

const syncFromStore = () => {
  const config = store.configFor(inboxId.value);
  if (!config) return;
  enabled.value = [...config.enabledOutcomes];
  requireBeforeResolve.value = config.requireBeforeResolve;
  required.value = [...config.resolveRequiredOutcomes];
  inherited.value = config.inherited;
};

onMounted(async () => {
  await store.fetch(inboxId.value, true);
  syncFromStore();
});

// template auto-unwraps refs, so expose key-based togglers instead of passing refs around
const toggleIn = (list, key, value) => {
  const next = list.value.filter(k => k !== key);
  if (value) next.push(key);
  list.value = next;
};
const toggleEnabled = (key, value) => toggleIn(enabled, key, value);
const toggleRequired = (key, value) => toggleIn(required, key, value);

const enabledCatalog = computed(() =>
  store.manualCatalog.filter(item => enabled.value.includes(item.key))
);
const isInvalid = computed(() => enabled.value.length === 0);

const save = async () => {
  try {
    await store.save(inboxId.value, {
      enabled_outcomes: enabled.value,
      require_before_resolve: requireBeforeResolve.value,
      resolve_required_outcomes: required.value.filter(k =>
        enabled.value.includes(k)
      ),
    });
    syncFromStore();
    useAlert(t('WASAPI_CAPI.SAVED'));
  } catch (error) {
    useAlert(error?.response?.data?.error || t('WASAPI_CAPI.SAVE_ERROR'));
  }
};
</script>

<template>
  <section class="flex flex-col gap-8">
    <header class="flex flex-col gap-2">
      <h2 class="text-lg font-medium text-n-slate-12">
        {{ t('WASAPI_CAPI.SETTINGS_TITLE') }}
      </h2>
      <p class="text-sm text-n-slate-11">
        {{ t('WASAPI_CAPI.CHANNEL_DESCRIPTION') }}
      </p>
    </header>

    <Banner v-if="inherited" color="slate">
      {{ t('WASAPI_CAPI.INHERITED_HINT') }}
    </Banner>

    <div class="flex flex-col gap-3">
      <h3 class="text-base font-medium text-n-slate-12">
        {{ t('WASAPI_CAPI.OUTCOMES_TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-11">
        {{ t('WASAPI_CAPI.OUTCOMES_HELP') }}
      </p>
      <ul class="grid grid-cols-1 gap-2 pl-0 list-none sm:grid-cols-2">
        <li
          v-for="item in store.manualCatalog"
          :key="item.key"
          class="rounded-xl outline outline-1 outline-n-weak bg-n-solid-1"
        >
          <label class="flex items-center gap-3 p-3 cursor-pointer">
            <Checkbox
              :model-value="enabled.includes(item.key)"
              @update:model-value="value => toggleEnabled(item.key, value)"
            />
            <span class="flex flex-col min-w-0">
              <span class="flex items-center gap-1.5 text-sm text-n-slate-12">
                <Icon
                  :icon="outcomeMeta(item.key).icon"
                  class="size-4 text-n-slate-11"
                />
                {{ t(outcomeLabelKey(item.key)) }}
              </span>
              <span class="text-xs text-n-slate-10">
                {{
                  `${t('WASAPI_CAPI.EVENT')}: ${
                    item.event_name || t('WASAPI_CAPI.NO_EVENT')
                  }${item.requires_value ? ` · ${t('WASAPI_CAPI.REQUIRES_VALUE')}` : ''}`
                }}
              </span>
            </span>
          </label>
        </li>
      </ul>
    </div>

    <div
      class="flex flex-col gap-3 p-4 rounded-xl outline outline-1 outline-n-weak bg-n-solid-1"
    >
      <div class="flex items-start justify-between gap-4">
        <div class="flex flex-col gap-1">
          <h3 class="text-base font-medium text-n-slate-12">
            {{ t('WASAPI_CAPI.REQUIRE_TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-11">
            {{ t('WASAPI_CAPI.REQUIRE_HELP') }}
          </p>
        </div>
        <Switch v-model="requireBeforeResolve" />
      </div>
      <div v-if="requireBeforeResolve" class="flex flex-col gap-2 pt-2">
        <span class="text-sm text-n-slate-11">{{
          t('WASAPI_CAPI.REQUIRED_OUTCOMES')
        }}</span>
        <div class="flex flex-wrap gap-2">
          <label
            v-for="item in enabledCatalog"
            :key="item.key"
            class="flex items-center gap-2 px-3 py-1.5 text-sm rounded-full outline outline-1 cursor-pointer"
            :class="
              required.includes(item.key)
                ? 'outline-n-brand bg-n-brand/10 text-n-slate-12'
                : 'outline-n-weak text-n-slate-11'
            "
          >
            <input
              type="checkbox"
              class="sr-only"
              :checked="required.includes(item.key)"
              @change="event => toggleRequired(item.key, event.target.checked)"
            />
            {{ t(outcomeLabelKey(item.key)) }}
          </label>
        </div>
      </div>
    </div>

    <div class="flex justify-end">
      <Button
        :label="t('WASAPI_CAPI.SAVE')"
        color="blue"
        :disabled="isInvalid || store.isSaving"
        :is-loading="store.isSaving"
        @click="save"
      />
    </div>
  </section>
</template>
